import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  Table,
  Tag,
  Spin,
  Empty,
  Input,
  InputNumber,
  Button,
  Modal,
  Switch,
  Select,
  Tooltip,
  Divider,
  Typography,
  Space,
} from 'antd';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  SaveOutlined,
  SettingOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { InputDefinitionTabProps } from '../types';
import type { ColumnsType } from 'antd/es/table';
import type { SchemaField } from '../../ProcessDefinition/types';
import { STRING_FORMATS } from '../../ProcessDefinition/types';
import { useSchemaEditor } from '../hooks/useSchemaEditor';

interface OutputDefinitionTabPropsExtended
  extends Omit<InputDefinitionTabProps, 'onSave'> {
  ontologyId: number;
  definition?: any;
  onSave?: (data: SchemaField[]) => void;
}

const OutputDefinitionTab = forwardRef<any, OutputDefinitionTabPropsExtended>(
  ({ nodeId, ontologyId, definition, onSave, onHasChangesChange }, ref) => {
    const {
      schemaState,
      expandedKeys,
      advancedState,
      operations,
      toggleExpand,
      openAdvancedEditor,
      closeAdvancedEditor,
      save,
    } = useSchemaEditor({
      nodeId: nodeId!,
      ontologyId,
      definition,
      type: 'output',
      onSave,
    });

    // 解构操作函数
    const { updateDescription, toggleRequired, toggleArray, updateField } =
      operations;
    const { dataSource, loading, hasChanges } = schemaState;

    // 通知父组件状态变化
    useEffect(() => {
      onHasChangesChange?.(hasChanges);
    }, [hasChanges, onHasChangesChange]);

    // 暴露save方法给父组件
    useImperativeHandle(
      ref,
      () => ({
        save,
      }),
      [save]
    );
    const { visible: advancedModalVisible, currentField: currentEditField } =
      advancedState;

    // 处理函数包装
    const handleUpdateDescription = useCallback(
      (key: string, description: string) => {
        updateDescription(key, description);
      },
      [updateDescription]
    );

    const handleToggleRequired = useCallback(
      (key: string) => {
        toggleRequired(key);
      },
      [toggleRequired]
    );

    const handleToggleArray = useCallback(
      (key: string, isArray: boolean) => {
        toggleArray(key);
      },
      [toggleArray]
    );

    const handleOpenAdvanced = useCallback(
      (record: SchemaField) => {
        openAdvancedEditor(record);
      },
      [openAdvancedEditor]
    );

    const handleCloseAdvanced = useCallback(() => {
      closeAdvancedEditor();
    }, [closeAdvancedEditor]);

    const handleSave = useCallback(async () => {
      await save();
    }, [save]);

    const [enumInput, setEnumInput] = useState('');

    const updateFieldProperty = useCallback(
      (key: string, value: any) => {
        if (!currentEditField) return;
        const targetKey =
          (currentEditField as any).originalKey || currentEditField.key;
        updateField(targetKey, { [key]: value });
      },
      [currentEditField, updateField]
    );

    const updateConstraintProperty = useCallback(
      (constraintKey: string, value: any) => {
        if (!currentEditField) return;
        const targetKey =
          (currentEditField as any).originalKey || currentEditField.key;
        updateField(targetKey, { constraints: { [constraintKey]: value } });
      },
      [currentEditField, updateField]
    );

    // 渲染嵌套数据 - 使用稳定的key生成策略
    const renderNestedData = useCallback(
      (
        data: SchemaField[],
        level: number = 0,
        parentKey: string = ''
      ): any[] => {
        const result: any[] = [];
        data.forEach((item, index) => {
          const uniqueKey = parentKey ? `${parentKey}.${item.key}` : item.key;
          const finalKey = `${uniqueKey}_${level}_${index}`; // 稳定的key生成，不使用时间戳

          const flatItem = {
            ...item,
            key: finalKey,
            originalKey: item.key,
            level,
            _children: item.children || [],
            children: undefined,
          };

          result.push(flatItem);
          if (
            item.children &&
            item.children.length > 0 &&
            expandedKeys.has(item.key)
          ) {
            result.push(
              ...renderNestedData(item.children, level + 1, uniqueKey)
            );
          }
        });
        return result;
      },
      [expandedKeys]
    );

    const flatData = useMemo(() => {
      return renderNestedData(dataSource);
    }, [dataSource, renderNestedData]);

    // 获取来源标签
    const getSourceTag = (field: SchemaField) => {
      // 优先使用sourceLink，如果不存在则使用sourceType
      const sourceLink = (field as any).sourceLink;
      const sourceType = (field as any).sourceType;

      console.log('[OutputDefinitionTab] getSourceTag', {
        fieldName: field.name,
        sourceLink,
        sourceType,
        field,
      });

      if (sourceLink) {
        return sourceLink;
      }

      if (sourceType === 'OBJECT') return 'consume';
      if (sourceType === 'ATTRIBUTE') return 'instrument';
      return 'consume';
    };

    const columns: ColumnsType<SchemaField & { level: number }> = [
      {
        title: '参数名称',
        dataIndex: 'name',
        key: 'name',
        width: 200,
        render: (text, record) => {
          const hasChildren =
            record.type === 'object' &&
            (record as any)._children &&
            (record as any)._children.length > 0;
          const isExpanded = expandedKeys.has(
            (record as any).originalKey || record.key
          );

          return (
            <div
              style={{ paddingLeft: `${record.level * 20}px` }}
              className="flex items-center gap-2"
            >
              {/* 只有有子级的object类型才显示箭头 */}
              {hasChildren && (
                <span
                  onClick={() =>
                    toggleExpand((record as any).originalKey || record.key)
                  }
                  className="cursor-pointer text-gray-500 hover:text-blue-500"
                  title={isExpanded ? '点击收起' : '点击展开'}
                >
                  {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                </span>
              )}

              {/* 如果没有子级，显示占位符保持对齐 */}
              {!hasChildren && (
                <span style={{ width: '14px', display: 'inline-block' }}></span>
              )}

              <span className="text-sm">{text || '-'}</span>
            </div>
          );
        },
      },
      {
        title: '类型',
        dataIndex: 'type',
        key: 'type',
        width: 180,
        filters: [
          { text: 'string', value: 'string' },
          { text: 'number', value: 'number' },
          { text: 'integer', value: 'integer' },
          { text: 'boolean', value: 'boolean' },
          { text: 'array', value: 'array' },
          { text: 'object', value: 'object' },
        ],
        onFilter: (value: any, record) => record.type === value,
        render: (text, record) => {
          const recordKey = (record as any).originalKey || record.key;
          const isObject = text === 'object';
          const isArray = record.isArray;

          return (
            <div className="flex items-center justify-center gap-2">
              {/* 类型显示 */}
              <span
                className="text-sm"
                style={{
                  color:
                    text === 'string'
                      ? '#52c41a'
                      : text === 'number' || text === 'integer'
                        ? '#1890ff'
                        : text === 'boolean'
                          ? '#fa8c16'
                          : text === 'array'
                            ? '#722ed1'
                            : text === 'object'
                              ? '#13c2c2'
                              : '#999',
                  fontFamily: 'monospace',
                }}
              >
                {text}
              </span>

              {/* object类型的数组图标切换 - 替换设置图标 */}
              {isObject ? (
                <Tooltip
                  key={`array-${recordKey}`}
                  placement="top"
                  title={isArray ? '当前为多个' : '当前为单个'}
                >
                  <span
                    className="cursor-pointer font-mono text-sm"
                    style={{
                      color: isArray ? '#1890ff' : '#d9d9d9',
                      fontWeight: isArray ? 'bold' : 'normal',
                    }}
                    onClick={() => handleToggleArray(recordKey, !isArray)}
                  >
                    []
                  </span>
                </Tooltip>
              ) : (
                /* 非object类型显示设置图标 */
                <Tooltip
                  key={`setting-${recordKey}`}
                  placement="top"
                  title="高级选项"
                >
                  <SettingOutlined
                    className="cursor-pointer text-gray-400 hover:text-blue-500"
                    style={{ fontSize: '14px' }}
                    onClick={() => handleOpenAdvanced(record)}
                  />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: '必填',
        dataIndex: 'required',
        key: 'required',
        width: 80,
        align: 'center',
        filters: [
          { text: '是', value: true },
          { text: '否', value: false },
        ],
        onFilter: (value: any, record) => record.required === value,
        render: text => <span className="text-sm">{text ? '是' : '否'}</span>,
      },
      {
        title: '说明',
        dataIndex: 'description',
        key: 'description',
        render: (text, record) => {
          const recordKey = (record as any).originalKey || record.key;
          return (
            <Input
              key={`desc-${recordKey}`}
              value={text}
              onChange={e => handleUpdateDescription(recordKey, e.target.value)}
              placeholder="请输入说明"
              size="small"
              bordered={false}
              className="hover:border-gray-300"
            />
          );
        },
      },
      {
        title: '来源',
        dataIndex: 'source',
        key: 'source',
        width: 200,
        render: (_, record) => {
          // 只有一级元素展示来源，子元素不展示
          if (record.level > 0) return null;

          const source = getSourceTag(record);
          const sourceLink = (record as any).sourceLink;

          // 根据source值确定颜色
          const getTagColor = (sourceValue: string) => {
            if (sourceValue === 'consume' || sourceValue === 'consumption')
              return 'blue';
            if (sourceValue === 'instrument') return 'green';
            return 'pink';
          };

          return (
            <div className="flex flex-col gap-1">
              <Tag
                color={getTagColor(source)}
                style={{
                  margin: '1px 6px',
                  width: 'fit-content',
                }}
              >
                {source}
              </Tag>
              {sourceLink && sourceLink !== source && (
                <Tooltip title={sourceLink} placement="topLeft">
                  <span className="text-xs text-gray-500 truncate max-w-[180px] block">
                    {sourceLink}
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      },
    ];

    return (
      <div className="p-4">
        <Spin spinning={loading}>
          {/* 标题和说明 */}
          <div className="mb-4 p-4 bg-blue-50 rounded">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-blue-600 font-medium">
                ℹ️ 输出参数来源规则
              </span>
            </div>
            <div className="text-sm text-gray-600">
              输出参数根据OPD中的
              <span className="font-medium">Result关系</span>
              （结果）自动获取
            </div>
          </div>

          {/* 表格 */}
          {dataSource.length > 0 ? (
            <>
              <Table
                columns={columns}
                dataSource={flatData}
                rowKey="key"
                pagination={false}
                size="small"
                bordered
                className="mb-4"
              />

              {/* 保存按钮 */}
              {hasChanges && (
                <div className="flex justify-end">
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={loading}
                  >
                    保存说明
                  </Button>
                </div>
              )}
            </>
          ) : (
            !loading && (
              <Empty
                description="暂无输出参数定义"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          )}
        </Spin>

        {/* 高级选项Modal */}
        <Modal
          title={null}
          open={advancedModalVisible}
          onCancel={handleCloseAdvanced}
          footer={null}
          width={450}
          closeIcon={<span style={{ fontSize: '20px' }}>×</span>}
        >
          {currentEditField && (
            <div>
              {/* 字段名 + 类型 */}
              <div style={{ marginBottom: 8 }}>
                <Typography.Text strong style={{ fontSize: 13 }}>
                  {currentEditField.name}
                </Typography.Text>
                <span
                  style={{
                    color:
                      (
                        {
                          string: '#52c41a',
                          number: '#1890ff',
                          integer: '#1890ff',
                          boolean: '#fa8c16',
                          array: '#722ed1',
                          object: '#13c2c2',
                        } as any
                      )[currentEditField.type] || '#999',
                    marginLeft: 8,
                    fontSize: 12,
                    fontFamily: 'monospace',
                  }}
                >
                  {currentEditField.type}
                </span>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              {/* 必需开关 */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  <Switch
                    size="small"
                    checked={currentEditField.required || false}
                    onChange={v => updateFieldProperty('required', v)}
                  />
                  <span>必需</span>
                </label>
              </div>

              {/* string 类型约束 */}
              {currentEditField.type === 'string' && (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        display: 'block',
                        marginBottom: 4,
                      }}
                    >
                      format
                    </Typography.Text>
                    <Select
                      size="small"
                      placeholder="选择格式"
                      value={currentEditField.constraints?.format || undefined}
                      onChange={v =>
                        updateConstraintProperty('format', v || undefined)
                      }
                      options={STRING_FORMATS}
                      allowClear
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <Typography.Text
                        type="secondary"
                        style={{
                          fontSize: 11,
                          display: 'block',
                          marginBottom: 4,
                        }}
                      >
                        最小长度
                      </Typography.Text>
                      <InputNumber
                        size="small"
                        placeholder=">= 0"
                        min={0}
                        value={currentEditField.constraints?.minLength}
                        onChange={v =>
                          updateConstraintProperty('minLength', v ?? undefined)
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Typography.Text
                        type="secondary"
                        style={{
                          fontSize: 11,
                          display: 'block',
                          marginBottom: 4,
                        }}
                      >
                        最大长度
                      </Typography.Text>
                      <InputNumber
                        size="small"
                        placeholder=">= 0"
                        min={0}
                        value={currentEditField.constraints?.maxLength}
                        onChange={v =>
                          updateConstraintProperty('maxLength', v ?? undefined)
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        display: 'block',
                        marginBottom: 4,
                      }}
                    >
                      pattern
                    </Typography.Text>
                    <Input
                      size="small"
                      placeholder="^[A-Za-z0-9_]+$"
                      value={currentEditField.constraints?.pattern}
                      onChange={e =>
                        updateConstraintProperty(
                          'pattern',
                          e.target.value || undefined
                        )
                      }
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </div>

                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        display: 'block',
                        marginBottom: 4,
                      }}
                    >
                      枚举值
                    </Typography.Text>
                    {(currentEditField.constraints?.enum || []).length > 0 && (
                      <Space size={4} wrap style={{ marginBottom: 4 }}>
                        {(currentEditField.constraints?.enum || []).map(
                          (val: string) => (
                            <Tag
                              key={val}
                              closable
                              onClose={() =>
                                updateConstraintProperty(
                                  'enum',
                                  (
                                    currentEditField.constraints?.enum || []
                                  ).filter((v: string) => v !== val).length > 0
                                    ? (
                                        currentEditField.constraints?.enum || []
                                      ).filter((v: string) => v !== val)
                                    : undefined
                                )
                              }
                              style={{ fontSize: 11 }}
                            >
                              {val}
                            </Tag>
                          )
                        )}
                      </Space>
                    )}
                    <Space size={4}>
                      <Input
                        size="small"
                        placeholder="添加枚举值"
                        value={enumInput}
                        onChange={e => setEnumInput(e.target.value)}
                        onPressEnter={() => {
                          const trimmed = enumInput.trim();
                          const curr = currentEditField.constraints?.enum || [];
                          if (trimmed && !curr.includes(trimmed)) {
                            updateConstraintProperty('enum', [
                              ...curr,
                              trimmed,
                            ]);
                            setEnumInput('');
                          }
                        }}
                        style={{ width: 130, fontSize: 12 }}
                      />
                      <Button
                        size="small"
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          const trimmed = enumInput.trim();
                          const curr = currentEditField.constraints?.enum || [];
                          if (trimmed && !curr.includes(trimmed)) {
                            updateConstraintProperty('enum', [
                              ...curr,
                              trimmed,
                            ]);
                            setEnumInput('');
                          }
                        }}
                        style={{ fontSize: 11 }}
                      />
                    </Space>
                  </div>
                </>
              )}

              {/* number / integer 类型约束 */}
              {(currentEditField.type === 'number' ||
                currentEditField.type === 'integer') && (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <Typography.Text
                        type="secondary"
                        style={{
                          fontSize: 11,
                          display: 'block',
                          marginBottom: 4,
                        }}
                      >
                        最小值
                      </Typography.Text>
                      <InputNumber
                        size="small"
                        placeholder="minimum"
                        value={currentEditField.constraints?.minimum}
                        onChange={v =>
                          updateConstraintProperty('minimum', v ?? undefined)
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Typography.Text
                        type="secondary"
                        style={{
                          fontSize: 11,
                          display: 'block',
                          marginBottom: 4,
                        }}
                      >
                        最大值
                      </Typography.Text>
                      <InputNumber
                        size="small"
                        placeholder="maximum"
                        value={currentEditField.constraints?.maximum}
                        onChange={v =>
                          updateConstraintProperty('maximum', v ?? undefined)
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: 16,
                      marginBottom: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <Switch
                        size="small"
                        checked={
                          currentEditField.constraints?.exclusiveMinimum !==
                          undefined
                        }
                        onChange={v =>
                          updateConstraintProperty(
                            'exclusiveMinimum',
                            v
                              ? (currentEditField.constraints?.minimum ?? 0)
                              : undefined
                          )
                        }
                      />
                      <span>排除最小值</span>
                    </label>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <Switch
                        size="small"
                        checked={
                          currentEditField.constraints?.exclusiveMaximum !==
                          undefined
                        }
                        onChange={v =>
                          updateConstraintProperty(
                            'exclusiveMaximum',
                            v
                              ? (currentEditField.constraints?.maximum ?? 0)
                              : undefined
                          )
                        }
                      />
                      <span>排除最大值</span>
                    </label>
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        display: 'block',
                        marginBottom: 4,
                      }}
                    >
                      倍数 (multipleOf)
                    </Typography.Text>
                    <InputNumber
                      size="small"
                      placeholder="multipleOf"
                      min={0}
                      value={currentEditField.constraints?.multipleOf}
                      onChange={v =>
                        updateConstraintProperty('multipleOf', v ?? undefined)
                      }
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        display: 'block',
                        marginBottom: 4,
                      }}
                    >
                      枚举值
                    </Typography.Text>
                    {(currentEditField.constraints?.enum || []).length > 0 && (
                      <Space size={4} wrap style={{ marginBottom: 4 }}>
                        {(currentEditField.constraints?.enum || []).map(
                          (val: string) => (
                            <Tag
                              key={val}
                              closable
                              onClose={() =>
                                updateConstraintProperty(
                                  'enum',
                                  (
                                    currentEditField.constraints?.enum || []
                                  ).filter((v: string) => v !== val).length > 0
                                    ? (
                                        currentEditField.constraints?.enum || []
                                      ).filter((v: string) => v !== val)
                                    : undefined
                                )
                              }
                              style={{ fontSize: 11 }}
                            >
                              {val}
                            </Tag>
                          )
                        )}
                      </Space>
                    )}
                    <Space size={4}>
                      <Input
                        size="small"
                        placeholder="添加枚举值"
                        value={enumInput}
                        onChange={e => setEnumInput(e.target.value)}
                        onPressEnter={() => {
                          const trimmed = enumInput.trim();
                          const curr = currentEditField.constraints?.enum || [];
                          if (trimmed && !curr.includes(trimmed)) {
                            updateConstraintProperty('enum', [
                              ...curr,
                              trimmed,
                            ]);
                            setEnumInput('');
                          }
                        }}
                        style={{ width: 130, fontSize: 12 }}
                      />
                      <Button
                        size="small"
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          const trimmed = enumInput.trim();
                          const curr = currentEditField.constraints?.enum || [];
                          if (trimmed && !curr.includes(trimmed)) {
                            updateConstraintProperty('enum', [
                              ...curr,
                              trimmed,
                            ]);
                            setEnumInput('');
                          }
                        }}
                        style={{ fontSize: 11 }}
                      />
                    </Space>
                  </div>
                </>
              )}

              {/* array / isArray 数量约束 */}
              {(currentEditField.type === 'array' ||
                (currentEditField as any).isArray) && (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        display: 'block',
                        marginBottom: 6,
                      }}
                    >
                      数量限制
                    </Typography.Text>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      <InputNumber
                        size="small"
                        placeholder="最少"
                        min={0}
                        value={currentEditField.constraints?.minItems ?? null}
                        onChange={v =>
                          updateConstraintProperty('minItems', v ?? undefined)
                        }
                        style={{ width: 80 }}
                      />
                      <span style={{ fontSize: 12, color: '#999' }}>~</span>
                      <InputNumber
                        size="small"
                        placeholder="无限制"
                        min={currentEditField.constraints?.minItems ?? 0}
                        value={currentEditField.constraints?.maxItems ?? null}
                        onChange={v =>
                          updateConstraintProperty('maxItems', v ?? undefined)
                        }
                        style={{ width: 80 }}
                      />
                      <span style={{ fontSize: 11, color: '#999' }}>个</span>
                    </div>
                  </div>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    <Switch
                      size="small"
                      checked={
                        currentEditField.constraints?.uniqueItems || false
                      }
                      onChange={v =>
                        updateConstraintProperty('uniqueItems', v || undefined)
                      }
                    />
                    <span>元素唯一</span>
                  </label>
                </>
              )}
            </div>
          )}
        </Modal>
      </div>
    );
  }
);

OutputDefinitionTab.displayName = 'OutputDefinitionTab';

export default OutputDefinitionTab;
